// src/pages/SearchPage.jsx
import React, { useState, useRef, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import SearchSection from "../components/layout/SearchSection";
import ResultsSection from "../components/ui/ResultsSection";
import SearchHeader from "../components/ui/SearchHeader";
import CourseScheduleModal from "../components/modals/CourseScheduleModal";
import CourseSyllabusViewerModal from "../components/modals/CourseSyllabusViewerModal";
import { useSearch } from "../contexts/SearchContext";

export default function SearchPage() {
  const [scheduleViewerOpen,   setScheduleViewerOpen  ] = useState(false);
  const [scheduleViewerCourse, setScheduleViewerCourse] = useState(null);
  const [syllabusViewCourse,   setSyllabusViewCourse  ] = useState(null);

  const resultsRef = useRef(null);

  const [searchParams] = useSearchParams();
  const { searchByProgram } = useSearch();

  // 300ms: long enough for the advanced-search panel's collapse animation
  // (250ms, see SearchSection.jsx) to finish before we measure/scroll to
  // the results section — scrolling while that panel is still collapsing
  // would leave the final scroll position off by the collapsed height.
  const scrollToResults = useCallback(() => {
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  }, []);

  // Landing here with ?programId=123 (e.g. the student wallet's
  // "register for this program's courses" button) jumps straight to
  // that program's own courses instead of the normal unfiltered
  // search — runs once on mount.
  useEffect(() => {
    const programId = Number(searchParams.get('programId'));
    if (programId > 0) {
      searchByProgram(programId);
      scrollToResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleShowSchedule = (course) => {
    if (!course) return;
    setScheduleViewerCourse(course);
    setScheduleViewerOpen(true);
  };

  const handleCloseSchedule = () => {
    setScheduleViewerCourse(null);
    setScheduleViewerOpen(false);
  };

  const handleViewSyllabus = (course) => {
    if (!course) return;
    const raw = String(course.syllabusUrl || course.syllabus_url || "").trim();
    if (!raw) { window.alert?.("אין סילבוס זמין לקורס זה"); return; }
    setSyllabusViewCourse({ ...course, syllabusUrl: raw });
  };

  return (
    <>
      <div className="md:min-h-screen bg-[#f4f5f8]" dir="rtl">

        {/* Header */}
        <SearchHeader />

        {/* Hero search band */}
        <div className="relative overflow-hidden bg-gray-100 px-0 pt-6 pb-8 sm:px-6 sm:pt-8 sm:pb-11 lg:px-8 lg:pt-10 lg:pb-13">

          {/* Light blob overlays — replaces ::before */}
          <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden="true"
            style={{
              backgroundImage:
                "radial-gradient(circle at 15% 50%, rgba(255,255,255,0.08) 0%, transparent 50%), radial-gradient(circle at 85% 20%, rgba(255,255,255,0.06) 0%, transparent 40%)",
            }}
          />

          {/* Dot-grid texture — replaces ::after */}
          <div
            className="absolute inset-0 pointer-events-none opacity-50"
            aria-hidden="true"
            style={{
              backgroundImage: "radial-gradient(rgba(255,255,255,0.12) 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          />

          {/* Search content */}
          <div className="relative mx-auto max-w-3xl px-2">
            <SearchSection onAfterSearch={scrollToResults} />
          </div>
        </div>

        {/* Results area */}
        <div ref={resultsRef} className="mx-auto max-w-8xl px-2 sm:px-4 md:px-6 lg:px-8 py-0">
          <ResultsSection
            onShowSchedule={handleShowSchedule}
            onViewSyllabus={handleViewSyllabus}
          />
        </div>
      </div>

      <CourseScheduleModal
        isOpen={scheduleViewerOpen}
        onClose={handleCloseSchedule}
        schedule={scheduleViewerCourse?.schedule}
        courseTitle={scheduleViewerCourse?.title || "קורס"}
      />

      <CourseSyllabusViewerModal
        isOpen={!!syllabusViewCourse}
        onClose={() => setSyllabusViewCourse(null)}
        courseTitle={syllabusViewCourse?.title}
        syllabusUrl={syllabusViewCourse?.syllabusUrl}
      />
    </>
  );
}